/**
 * useShowroomSync Hook
 *
 * Syncs user's preferred showroom between:
 * - Browser cookie (local storage for guests)
 * - SFCC customer profile (for logged-in users)
 *
 * Uses SCAPI Shopper Customers API for updating custom attributes.
 * Requires c_preferredShowroom to be "Externally Visible" in Business Manager.
 *
 * Flow:
 * 1. On login: Load c_preferredShowroom from account → save to cookie
 * 2. On showroom selection: Save to cookie, and if logged in → save to account via SCAPI
 */

import {useCallback, useEffect, useState} from 'react';

import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer';

import {
  getMyShowroomFromCookie,
  setMyShowroomToCookie,
  SHOWROOM_CHANGED_EVENT,
} from '@amblique/king-living/app/utils/utils';

/**
 * Hook to sync showroom preference between cookie and SFCC account
 *
 * @returns {Object} { syncShowroomToAccount, isLoading, error }
 */
export const useShowroomSync = () => {
  const {site} = useMultiSite();
  const siteId = site?.id;

  const {data: customer, isLoading: isCustomerLoading} = useCurrentCustomer();
  const {isRegistered, customerId} = customer || {};

  // Use SCAPI mutation for updating customer
  const updateCustomerMutation = useShopperCustomersMutation('updateCustomer');

  const [error, setError] = useState(null);

  // Sync showroom between cookie and account when user logs in
  useEffect(() => {
    if (!siteId || isCustomerLoading || !isRegistered || !customerId) return;

    const cookieShowroom = getMyShowroomFromCookie(siteId);
    const accountShowroomRaw = customer?.c_preferredShowroom;

    // Parse account showroom if exists
    let accountShowroom = null;

    if (accountShowroomRaw) {
      try {
        accountShowroom = typeof accountShowroomRaw === 'string' ?
          JSON.parse(accountShowroomRaw) :
          accountShowroomRaw;

        // Only use if it's for the current site
        if (accountShowroom && accountShowroom.siteId && accountShowroom.siteId !== siteId) {
          accountShowroom = null;
        }
      } catch {
        // Invalid JSON, ignore
        accountShowroom = null;
      }
    }

    // Case 1: Account has showroom, cookie doesn't or is different → sync account to cookie
    if (accountShowroom && (!cookieShowroom || cookieShowroom.id !== accountShowroom.id)) {
      setMyShowroomToCookie(siteId, accountShowroom);
    } else if (cookieShowroom && !accountShowroom) {
      // Case 2: Cookie has showroom, account doesn't → sync cookie to account
      // Add siteId to the showroom data for multi-site support
      const showroomData = {
        ...cookieShowroom,
        siteId,
      };

      // Sync cookie showroom to account
      updateCustomerMutation.mutateAsync({
        parameters: {customerId},
        body: {
          c_preferredShowroom: JSON.stringify(showroomData),
        },
      }).catch((err) => {
        console.error('[ShowroomSync] Failed to sync cookie showroom to account:', err);
      });
    }
  }, [siteId, isRegistered, customerId, customer?.c_preferredShowroom, isCustomerLoading, updateCustomerMutation]);

  // Function to sync showroom to account using SCAPI
  const syncShowroomToAccount = useCallback(
    async (showroom) => {
      if (!isRegistered || !customerId || !showroom) {
        return false;
      }

      setError(null);

      try {
        // Add siteId to the showroom data for multi-site support
        const showroomData = {
          ...showroom,
          siteId,
        };

        // Use SCAPI to update customer with custom attribute
        await updateCustomerMutation.mutateAsync({
          parameters: {customerId},
          body: {
            // Custom attributes are prefixed with c_
            c_preferredShowroom: JSON.stringify(showroomData),
          },
        });

        return true;
      } catch (err) {
        setError(err);

        return false;
      }
    },
    [isRegistered, customerId, siteId, updateCustomerMutation]
  );

  // Listen for showroom changes and auto-sync to account
  useEffect(() => {
    if (!isRegistered || !customerId) return;

    const handleShowroomChanged = async (event) => {
      const showroom = event.detail?.showroom;

      if (showroom) {
        await syncShowroomToAccount(showroom);
      }
    };

    window.addEventListener(SHOWROOM_CHANGED_EVENT, handleShowroomChanged);

    return () => {
      window.removeEventListener(SHOWROOM_CHANGED_EVENT, handleShowroomChanged);
    };
  }, [isRegistered, customerId, syncShowroomToAccount]);

  return {
    syncShowroomToAccount,
    isLoading: updateCustomerMutation.isPending,
    error,
    isRegistered,
  };
};

export default useShowroomSync;
